/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
/* eslint-disable arrow-body-style */
import React, {useState} from 'react';
import {ListGroup, InputGroup, FormControl} from 'react-bootstrap';
import type {
	IActionForm,
	ConditionObject,
	IPccRuleFormDynamic,
	IPccRuleFormStatic,
} from 'interfaces';
import {getNestedField} from 'pages/Policy/components/forms/utils/InitActionLabels';

import type {TransformedItem} from 'pages/Policy/components/forms/utils/InitActionLabels';

interface ListGroupActionProps {
	options: TransformedItem[];
	formData: IActionForm | IPccRuleFormDynamic | IPccRuleFormStatic;
	onSelect: (key: string, ...path: string[]) => void;
	resolveKey: (...labels: string[]) => string | string[] | undefined;
	nameDropdown: string;
}

const CustomListGroupAction = ({
	options,
	formData,
	onSelect,
	resolveKey,
	nameDropdown,
}: ListGroupActionProps) => {
	const [filter, setFilter] = useState('');

	/**
	 * Filters a hierarchical list of TransformedItem nodes based on a given text search.
	 *
	 * This function preserves the full tree structure needed to display a filtered view
	 * in a hierarchical UI such as a condition selector.
	 *
	 * Behavior:
	 * - If a node's category matches the filter string (case-insensitive), it is included
	 *   in the result along with all of its children (unfiltered).
	 * - If any child node matches the filter, the parent is included as context, and
	 *   only the matching children are retained.
	 * - If neither the node nor any of its descendants match the filter, the node is excluded.
	 * - Other properties such as `visibleSwitch`, `scope`, or `isVolTE` are preserved as-is.
	 *
	 * This ensures that:
	 * - Matching a **parent** category (e.g., "ARP") shows all its children (e.g., "ARP", "ARP").
	 * - Matching a **child** (e.g., "ARP") shows the full path including its ancestors (e.g., "Auth Def QoS > ARP > ARP").
	 * - Non-matching branches are pruned to keep the UI clean.
	 *
	 * @param items The top-level list of TransformedItem elements to filter.
	 * @param filterSelected The filter string entered by the user (typically from a search input).
	 * @returns A new array of TransformedItem elements that match the search directly or indirectly via descendants.
	 */
	const filterTransformedItems = (
		items: TransformedItem[],
		filterSelected: string,
	): TransformedItem[] => {
		const lowerFilter = filterSelected.toLowerCase();

		const filterRecursive = (item: TransformedItem): TransformedItem | null => {
			const selfMatch = item.category.toLowerCase().includes(lowerFilter);

			const processedChildren = (item.items || [])
				.map(filterRecursive)
				.filter((x): x is TransformedItem => x !== null);

			if (selfMatch) {
				return {
					...item,
					items: item.items ?? [],
				};
			}

			if (processedChildren.length > 0) {
				return {
					...item,
					items: processedChildren,
				};
			}

			return null;
		};

		return items
			.map(filterRecursive)
			.filter((item): item is TransformedItem => item !== null);
	};

	const filteredOptions = filter
		? filterTransformedItems(options, filter)
		: options;

	const isConditionObject = (
		maybeField: any,
	): maybeField is ConditionObject<any> => {
		return (
			typeof maybeField === 'object' &&
			maybeField !== null &&
			'value' in maybeField &&
			'active' in maybeField
		);
	};

	const computeIsActive = (path: string[]): boolean => {
		const fieldKey = resolveKey(...path);
		if (!fieldKey) return false;

		const field = Array.isArray(fieldKey)
			? getNestedField(formData, fieldKey)
			: formData[
					fieldKey as keyof (
						| IActionForm
						| IPccRuleFormDynamic
						| IPccRuleFormStatic
					)
			  ];

		if (typeof field === 'boolean') {
			return field;
		}

		if (isConditionObject(field)) {
			return field.active;
		}

		if (typeof field === 'object' && field !== null) {
			return Object.values(field).some(isConditionObjectAndActive);
		}

		return false;
	};

	const isConditionObjectAndActive = (val: any): val is ConditionObject<any> =>
		isConditionObject(val) && val.active;

	const renderItems = (
		items: TransformedItem[],
		parentPath: string[] = [],
	): React.ReactNode[] => {
		return items.map(item => {
			const isLeaf = !item.items || item.items.length === 0;
			const newPath = [...parentPath, item.category];

			if (isLeaf) {
				const isActive = computeIsActive(newPath);

				return (
					<ListGroup.Item
						key={newPath.join('-')}
						action
						className={`subitem d-flex justify-content-between align-items-center ${
							isActive ? 'active-item' : ''
						}`}
						onClick={e => {
							e.preventDefault();
							e.stopPropagation();
							onSelect(formData.id, ...newPath);
						}}>
						<div>
							<i className="bi bi-dot" />
							{item.category}
						</div>
					</ListGroup.Item>
				);
			} else {
				return (
					<React.Fragment key={newPath.join('-')}>
						<div className={`category-context level-${parentPath.length}`}>
							<ListGroup.Item className="category-header d-flex justify-content-between align-items-center category-header-fake-disabled">
								<div>
									<i className="bi bi-dash" />
									{item.category}
								</div>
							</ListGroup.Item>
							{renderItems(item.items, newPath)}
						</div>
					</React.Fragment>
				);
			}
		});
	};

	return (
		<div className="rounded-3 +-&gt; border" style={{height: '100%'}}>
			<div className="centered-div mb-2">
				<label htmlFor="filter-list-group" className="centered-label">
					{nameDropdown}
				</label>
			</div>
			<div className="mb-2 mr-3 ml-3 blur-div">
				<InputGroup className="w-auto input-with-icon">
					<FormControl
						id="filter-list-group"
						placeholder="Type to filter..."
						value={filter}
						onChange={(e: any) => setFilter(e.target.value)}
					/>
					<i className="bi bi-search" title="Search" data-toggle="tooltip" />
				</InputGroup>
			</div>
			<div className="scrollable-list &gt; bg-light my-3 mx-3 pr-3">
				<ListGroup>{renderItems(filteredOptions)}</ListGroup>
			</div>
		</div>
	);
};

export default CustomListGroupAction;
